
import React, {FC, MouseEvent, useState, useRef, useEffect, ChangeEvent, MutableRefObject}  from 'react';
import {useParams} from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Controller, FieldArray, FieldArrayMethodProps, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StringIfPlural, useTranslation  } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import { MdOutlineAdd } from 'react-icons/md';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import MoreIcon from '@mui/icons-material/More';


import entityService, { useBasicFilterEntity } from 'features/services/Entity';
import { currentBasicTextFilterPropsAtom, currentFormNameAtom, currentUserSessionAtom, isSearchBoxShowAtom, isSaveLoadingAtom } from 'library/store';
import useEmployeeService, { useBasicFilterEmployee } from './services/Employee';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { IEntity, IEntityNameId, IResult } from 'library/interface';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel, IconButton, InputAdornment, MenuItem, Tabs, Tab, Typography, Chip, FormHelperText, Link, ToggleButtonGroup, ToggleButton } from '@mui/material';
import NumberFormat from 'react-number-format';

import { IEmployee, defaultEmployee, IEmployeeSalaryItem } from './models/Employee';
import { ISalaryComponent } from './models/SalaryComponent';


import TextFieldRight from 'components/ui/TextFieldRight';
import { FormDialog } from 'components/ui/FormDialog';
import { BasicTextFilterForm } from 'components/ui/BasicTextFilterForm';
import useEnumerationService, { useBasicFilterEnumeration, useEnumerationItemCreateUpdate } from 'features/configuration/services/Enumeration';


import IEnumeration, { IEnumerationItem, Enum_REGISTRATION_STATUS, Enum_STUDY_TYPE,
  Enum_DEFAULT_LANGUAGE, Enum_TOWN, Enum_COUNTRY, Enum_PERSON_IDENTIFICATION, Enum_EMPLOYEE_TYPE, Enum_EMPLOYEE_CATEGORY,
    Enum_STUDY_LEVEL, Enum_STUDY_LANGUAGE, Enum_PERSON_GENDER, Enum_PERSON_CIVILITY, Enum_TEACHER_STATUS, Enum_TEACHER_DIPLOMA, Enum_TEACHER_SPECIALIZATION, Enum_TEACHER_FUNCTION, Enum_TEACHER_SERVICE } from 'features/configuration/models/Enumeration';
import {ITarification} from 'features/configuration/models/Tarification';


import { IExtensionType  } from 'features/configuration/models/ExtensionType';


import { carouselImage, justifyCenter, typographyGroupBoxStyling } from 'themes/commonStyles';

import EntityExpression from 'components/ui/EntityExpression';
import { isFalsy } from 'utility-types';

import useSchoolYearService, { useBasicFilterSchoolYear } from 'features/setup/services/SchoolYear';
import { ISchoolYear, ISchoolYearClass } from 'features/setup/models/SchoolYear';
import { DatePicker } from '@mui/x-date-pickers';
import { IClassTemplate } from 'features/setup/models/ClassTemplate';
import useClassTemplateService, { useBasicFilterClassTemplate } from 'features/setup/services/ClassTemplate';
import ArrayFieldTableEx, { ActionIconTableRow, HeadCell } from 'components/ui/ArrayFieldTableEx';
import { IBilling, defaultBilling } from 'features/finance/models/Billing';
import { IPerson } from 'features/production/models/Person';
import { useBasicFilterPerson } from 'features/production/services/Person';
import { IBillingType } from 'features/setup/models/BillingType';
import useSalaryComponentService from 'features/setup/services/SalaryComponent';
import ExtensionValueField from 'components/ui/ExtensionValueField';
import { DialogEnumerationItemForm } from 'features/configuration/DialogEnumerationItemForm';
import EnhancedTable from 'components/ui/EnhancedTable';


export const EmployeeForm: FC<IEmployee> = (props: IEmployee = defaultEmployee) => {

  const navigate = useNavigate();
  const { t, i18n } = useTranslation();  
  const { enqueueSnackbar } = useSnackbar();

  const {id} = useParams();

  const {language: lg} = useRecoilValue(currentUserSessionAtom);
  
  const [_id, _setId] = useState<number>( Number( id || 0 ) );

  const {retrieveEntity, retrieveData, openEntityActionDrawer, 
      checkEntityExpressionSyntax, checkEntitySaveAuthorization } = entityService();

  const {getEnumerationItemsByEnumerationCodes,getExtensionEnumerationItemsBySchoolYear, getAsOptions } = useEnumerationService();
  
  const { createEmployee, updateEmployee } = useEmployeeService();

  const { getSchoolYears } = useSchoolYearService();

  const { getAllSalaryComponents } = useSalaryComponentService();

  const { getClassTemplates } = useClassTemplateService();

  const [currentFormName, setCurrentFormNameAtom] = useRecoilState(currentFormNameAtom);
  const [isSaveLoading, setIsSaveLoading] = useRecoilState(isSaveLoadingAtom);

  //const refSchoolYears = useRef<ISchoolYear[]>();

  const [isSearchBoxShow, setIsSearchBoxShow] = useRecoilState(isSearchBoxShowAtom);
  const [currentBasicTextFilterProps, setCurrentBasicTextFilterProps] = useRecoilState(currentBasicTextFilterPropsAtom);
  const basicFilterEmployee = useBasicFilterEmployee( 
    (event: React.MouseEvent<unknown>, row: IEmployee) => {
        setIsSearchBoxShow(false);
        _setId(row.id);
      }
  );

  const {canAddEnumerationItem, canUpdateEnumerationItem, displayEnumerationItemDialog,
    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
    onAddItemClick, onUpdateItemClick } = useEnumerationItemCreateUpdate();

  const [openPersonFilter, setOpenPersonFilter] = useState(false);
  const basicFilterPerson = useBasicFilterPerson( 
      (event: React.MouseEvent<unknown>, row: IPerson) => {
          const {id, firstName, lastName, birthDate, birthPlace, particularSign} = row;
        
        setValue('personId', id);
        setValue('person.firstName', firstName);
        setValue('person.lastName', lastName);  
        setValue('person.birthDate', birthDate);   
        setValue('person.birthPlace', birthPlace);                    
        setValue('person.particularSign', particularSign);  
                          
        setOpenPersonFilter(false);
      }
  );

    
  const [openEntityFilter, setOpenEntityFilter] = useState(false);
  const basicFilterEntity = useBasicFilterEntity( 
      (event: React.MouseEvent<unknown>, row: IEntity) => {
          const {name, description} = row;

          // setValue('entityName', name || '');
          // setValue('entityDescription', description || '');
                           
          setOpenEntityFilter(false);
      }
  );

  const [openEnumerationFilter, setOpenEnumerationFilter] = useState(false);
  const basicFilterEnumeration = useBasicFilterEnumeration( 
      (event: React.MouseEvent<unknown>, row: IEnumeration) => {
          const {id, name, description} = row;

          // setValue('enumerationId', id);
          // setValue('enumerationName', name);
                           
          setOpenEnumerationFilter(false);
      }
  );
  
  const emptyFunc = (obj: any) => {}
  
  const methods = useForm<IEmployee>({defaultValues:defaultEmployee});
  const { register, setValue ,getValues, watch, reset ,handleSubmit ,control , formState: { errors } } = methods;

  const watchBase64Picture = watch('person.base64Picture');

  const watchStatus = watch('status');
  const watchLastDiploma = watch('lastDiploma');
  const watchSpecialization = watch('specialization');
  const watchServiceCode = watch('serviceCode');
  const watchFunctionCode = watch('functionCode');
  const watchEmployeeType = watch('employeeType');
  const watchEmployeeCategory = watch('employeeCategory');
  const watchHasSocialSecurity = watch('hasSocialSecurity');

   // let { fields, append: appendCoverages, update: updateCoverage ,remove: removeCoverage,  } = useFieldArray({//<O, TName>({ //<O,`billingDetails.${number}.billingDetailTasks`>({
  //   name: `productCoverages`,
  //   control,            
  // });
  

  const queryClient = useQueryClient();
  const {isLoading, isError, isSuccess ,error,mutate } = useMutation<IResult<IEmployee>,Error,IEmployee>(
      _id>0?updateEmployee:createEmployee, {   
        onSuccess: (data: IResult<IEmployee>) => {
          enqueueSnackbar( t('_Operation_done'), { variant: 'success',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1000 }); 
                   
          setIsSaveLoading(false);
          _setId(data.data.id);
          //setCurrentEntityIdForAction(data.data.id);
          setCurrentFormNameAtom(`${t('Employee')} - # ${data.data.id} # ` );
          //queryClient.invalidateQueries(['Employee',data.data.id]);
        },
        onError: (err: Error) => {          
          enqueueSnackbar( error?.message, { variant: 'error',
                anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
          setIsSaveLoading(false);
        }
      });

    const {data: _data, refetch} = useQuery<IEmployee>(['Employee', _id], () => retrieveEntity('Employee',_id), 
      {refetchOnWindowFocus: false ,enabled: false } );

    
    const {data: enumItems, refetch: refetchEnumerationItems} = useQuery<IEnumerationItem[]>( ['EnumerationItems', 'Employee'], () => getEnumerationItemsByEnumerationCodes
        ( [Enum_TEACHER_STATUS, Enum_TEACHER_DIPLOMA, Enum_TEACHER_SPECIALIZATION, Enum_TEACHER_SERVICE, Enum_TEACHER_FUNCTION,
          Enum_EMPLOYEE_TYPE, Enum_DEFAULT_LANGUAGE, Enum_COUNTRY, Enum_PERSON_IDENTIFICATION, Enum_EMPLOYEE_CATEGORY,
              Enum_PERSON_GENDER, Enum_PERSON_CIVILITY ] ));

    const {data: salaryComponents} = useQuery<ISalaryComponent[]>( ['SalaryComponents'], () => getAllSalaryComponents() );
    
    type EmployeeInfoView = 'absence' | 'discipline' | 'observation';
    const [employeeInfoView, setEmployeeInfoView] = useState<EmployeeInfoView>('absence');
    const handleChangeEmployeeInfoView = ( event: React.MouseEvent<HTMLElement>, newView: EmployeeInfoView) => {  

      if (newView === null) 
        return;
      
      setEmployeeInfoView(newView);           
    }


    const handleClickOpenPerson = async (event: any) => {
      setOpenPersonFilter(true);
    }
        
  
    function openFileDialog() {
      (document as any).getElementById("file-upload").click();
    }
  
    const setFile = (_event: any) => {
      let f = _event.target.files![0];
      var reader = new FileReader();
  
      reader.onload = function () {
  
          var binaryString = reader.result as string;
      
          const base64String = binaryString
                                      .replace('data:', '')
                                      .replace(/^.+,/, '');
  
          setValue("person.base64Picture", base64String);
          
        };
    
        reader.onerror = function () {
          console.log("File load failed");
        };
    
        reader.readAsDataURL(f);    
    };

    const [openEntityExpression, setOpenEntityExpression] = useState(false);
    const [currentExpression, setCurrentExpression] = useState(''); 
    const [currentEntityName, setCurrentEntityName] = useState(''); 
    const [currentReturnType, setCurrentReturnType] = useState(''); 

    type ExpressionType = 'salary-item' ;
    const [currentExpressionType, setCurrentExpressionType] = useState<ExpressionType>('salary-item');

    const handleClickOpenExpression = (event: any, expressionType: ExpressionType, 
        entityName: string, returnType: string, expression: string) => {

      setCurrentExpressionType(expressionType);
      setCurrentEntityName(entityName);
      setCurrentReturnType(returnType);
      setCurrentExpression(expression);
      setOpenEntityExpression(true);
    }

    const handleClickOkExpression = async () => {
            const checkExpression = await checkEntityExpressionSyntax(currentEntityName, currentExpression);
            if(!checkExpression){
              enqueueSnackbar( t('Expression evaluation error'), { variant: 'error',
                    anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
              return;
            }
    
            const {syntaxOk, syntaxError, returnType} = checkExpression;
            if(!syntaxOk) {
              enqueueSnackbar( syntaxError , { variant: 'error',
                    anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 }); 
              return;
            }
           
            if(returnType !== currentReturnType) {
              enqueueSnackbar( `${t('The result of expression must be')} ${currentReturnType}` , { variant: 'error',
                    anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 2000 });
                    return;
            }

            if( ['salary-item'].includes(currentExpressionType)  ) {
                const salaryItem = getValues().employeeSalaryItems.at(employeeSalaryItemIndex);
                if(isFalsy(salaryItem)) return;
            
                  const amountExpression = currentExpression;
                  alert(amountExpression);
                  
                  (refUpdateEmployeeSalaryItem.current??emptyFunc)( employeeSalaryItemIndex, 
                    {...salaryItem, amountExpression });
            }

            setOpenEntityExpression(false);
          }

    const handleAddEmployeeSalaryItems = (event: any) => {
        (refAppendEmployeeSalaryItems.current??emptyFunc)(
                  {id:0, employeeId: _id, salaryComponentId: 0, 
                    defaultAmount: 0, amountExpression: '' });
      }

    const cellEditableEmployeeSalaryItem = (row: IEmployeeSalaryItem, cellId: keyof IEmployeeSalaryItem) => {
        return true;
      }

      const getSalaryComponentList = (row: IEmployeeSalaryItem, cellId: keyof IEmployeeSalaryItem, 
          opts: {value: string|number, name: string}[]) => {        
          
          return (refSalaryComponents.current||[]).map(({id, name}) => ({value: id, name}) );
        }

        const getInputAdornmentEmployeeSalaryItem = (row: IEmployeeSalaryItem, cellId: keyof IEmployeeSalaryItem)  => ({
          
          toolTip: t('Expression'),
          icon: DeveloperModeIcon,
          onClickIcon: (event: any, index: number, row: IEmployeeSalaryItem ) => {

            const {amountExpression} = row;
            
            setEmployeeSalaryItemIndex(index);
            handleClickOpenExpression(event, 'salary-item', 'Employee', 'numeric', amountExpression);
            
          }  
        })

    const [headEmployeeSalaryItemCells, setHeadEmployeeSalaryItemCells]  = useState<HeadCell<IEmployeeSalaryItem>[]>([]);
        useEffect(() => {
        setHeadEmployeeSalaryItemCells([            
          {id:'id', label : t('Id'),  display: false, type: 'numeric', },
          
          {id:'salaryComponentId', label : t('Component'),  display: true, type: 'numeric', width: 40, 
              getOptions: getSalaryComponentList, isEditable: cellEditableEmployeeSalaryItem},
          
          {id:'defaultAmount', label : t('Amount'),  display: true, type: 'numeric', width: 20, 
              isEditable: cellEditableEmployeeSalaryItem},
          {id:'amountExpression', label : `${t('Expression')} - ${t('Amount')}`,  display: true, type: 'string', 
              width: 40, getInputAdornment: getInputAdornmentEmployeeSalaryItem},
        ]  )
      }, [t,i18n])
    
      const refAppendEmployeeSalaryItems = useRef<(value: Partial<FieldArray<IEmployee>> | Partial<FieldArray<IEmployee>>[], options?: FieldArrayMethodProps) => void>(null);
      const refUpdateEmployeeSalaryItem = useRef<(index: number,value: Partial<FieldArray<IEmployee>> ) => void>(null);
      const refRemoveEmployeeSalaryItem = useRef<(index: number ) => void>(null);
    
      const [employeeSalaryItemIndex, setEmployeeSalaryItemIndex] = useState<number>(-1);
      const employeeSalaryItemRowActionIcon = ( employeeSalaryItem: IEmployeeSalaryItem) : ActionIconTableRow<IEmployee,IEmployeeSalaryItem> => {
      
        const res: ActionIconTableRow<IEmployee,IEmployeeSalaryItem> = {
          toolTip: 'Remove',
          icon: RemoveCircleIcon,
          hasAction: (index: number,row: IEmployeeSalaryItem) => true, // ((optionPropertyName1 || '') !== '') || ((optionPropertyName2 || '') !== '') || ((optionPropertyName3 || '') !== ''),
          isActionExecuting: true,
          onRowClickIcon: (event : any,index: number, row: IEmployeeSalaryItem) => {
            
            (refRemoveEmployeeSalaryItem.current??emptyFunc)(index);            
          }
        }
        return res;
      }

    const refEnumItems = useRef<IEnumerationItem[]>([]);    
    useEffect( () => {   
        refEnumItems.current = enumItems || [];
      
    }, [enumItems]);

    const refSalaryComponents = useRef<ISalaryComponent[]>([]);    
    useEffect( () => {   
        refSalaryComponents.current = salaryComponents || [];
      
    }, [salaryComponents]);

      useEffect( () => {        
        setCurrentFormNameAtom(t('Employee'));
        setCurrentBasicTextFilterProps(basicFilterEmployee);
      }, []);    
    
      /********** This use effect call retreive data wich will call refetch and _data will be updated. 
        and the new useEffect will take place ********************/
        useEffect( () => {
            // setCurrentFormName(t('Billing'));        
            
            if(_id > 0)
              retrieveData('Employee',_id, refetch);  
          }, [_id] );
    
    
        useEffect( () => {
            
        if(_data && _data.id > 0) {
            setCurrentFormNameAtom(`${t('Employee')} - # ${_data.id} #` );
            reset(_data);
        }
        }, [_data]);
    
      const newData = async (event: MouseEvent<HTMLButtonElement>) => {    
        _setId(0);      
        reset(defaultEmployee);    
      }
      
      const saveData = async (event: MouseEvent<HTMLButtonElement>) => {        
        if(!checkEntitySaveAuthorization('Employee', _id)) {
          setIsSaveLoading(false);
             return;
        }
        
        
          const data = getValues(); 
          if(data.person.firstName.trim() === '' && data.person.lastName.trim() === '') {
              enqueueSnackbar( t('Employee information is not specied'), { variant: 'warning',
                    anchorOrigin : { horizontal: 'center', vertical: 'top' }, autoHideDuration : 1500 }); 
                    setIsSaveLoading(false);
                    return;
            }
      
          mutate(data);
      }

    const [selectedAbsenceIds, setSelectedAbsenceIds] = useState<number[]>([]);
    const [selectedDisciplineIds, setSelectedDisciplineIds] = useState<number[]>([]);
    const [selectedObservationIds, setSelectedObservationIds] = useState<number[]>([]);
    
    const actionData = async (event: MouseEvent<HTMLButtonElement>) => {

      const arrDisciplinaries = selectedDisciplineIds.map( id => ({entityName: 'EmployeeDisciplinarySheet', entityId: id}) );
      const arrAbsences = selectedAbsenceIds.map( id => ({entityName: 'EmployeeAbsenceSheet', entityId: id}) );
      const arrObservations = selectedObservationIds.map( id => ({entityName: 'EmployeeObservationSheet', entityId: id}) );
 
      setContextualItems([...arrDisciplinaries, ...arrAbsences, ...arrObservations]);
      openEntityActionDrawer('Employee', _id, contextualItems);      
    }

   
    const [contextualItems, setContextualItems] = useState<IEntityNameId[]>([]);

    
   
      // const actionData = async (event: MouseEvent<HTMLButtonElement>) => {
      //   openEntityActionDrawer('Employee', _id);
      // }
      
    const afterAction = async (event: MouseEvent<HTMLButtonElement>) => {          
    //    queryClient.invalidateQueries(['RequestType',currentEntityIdForAction]);        
    //    await retreiveData(currentEntityNameForAction,currentEntityIdForAction, refetch);        
    //    reset(_data);        
    }

  return (
    <FormProvider {...methods} >
            <Box sx={{ mx: 0.1 }}>
                <Grid container rowSpacing={0.5} columnSpacing={0.1}>
                    <Grid item xs={12} md={7} lg={7} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >                        
                        <Stack flexDirection='column'  >
                            <Box sx={{ mt: 0.5, width: '100%' }} >
                                <Button id='btnNew' onClick={newData} sx={ {display:'none'}}  />                                  
                                <Button id='btnSave' onClick={saveData} sx={ {display:'none'}}  />
                                <Button id='btnAction' onClick={actionData} sx={ {display:'none'}}  />                                                              
                                <Button id='btnAfterAction' onClick={afterAction} sx={ {display:'none'}}  />
                                
                                <TextField sx={{width:'calc(10% - 8px)'}} id="id" label={t('Regis. Number')} {...register('registrationNumber')} />
                                <Controller control={control}
                                  name='hiringDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('Hiring date')} 
                                      onChange={onChange}                     
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(15% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(15% - 8px)'}} />}
                                    /> )}
                                /> 
                                <Controller name='status' control={control} 
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(15% - 8px)'}} id="status"
                                        label={t('Status')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_TEACHER_STATUS, 
                                                                  `${t('Employee')} - ${t('Status')}`, `${t('Employee')} - ${t('Status')}`)}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchStatus) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_TEACHER_STATUS, `${t('Employee')} - ${t('Status')}`, `${t('Employee')} - ${t('Status')}`,
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_TEACHER_STATUS && x.code === watchStatus)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_TEACHER_STATUS ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                /> 
                                <Controller name='salaryPeriodicity' control={control} 
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(15% - 8px)'}} id="salaryPeriodicity"
                                        label={t('Salary periodicity')} inputProps={ {readOnly: false}} 
                                         >
                                        <MenuItem key='d' value='d'>{t('Dayly')}</MenuItem>
                                        <MenuItem key='w' value='w'>{t('Weekly')}</MenuItem>
                                        <MenuItem key='M' value='M'>{t('Monthly')}</MenuItem>
                                      </TextField>
                                    )}
                                /> 
                                
                                                                                            
                                <Controller name='employeeType' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(20% - 8px)'}} id="employeeType"
                                        label={t('Empl. type')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_EMPLOYEE_TYPE, 
                                                                  `${t('Employee')} - ${t('Type')}`, `${t('Employee')} - ${t('Type')}`)}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchEmployeeType) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_EMPLOYEE_TYPE, `${t('Employee')} - ${t('Type')}`, `${t('Employee')} - ${t('Type')}`,
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_EMPLOYEE_TYPE && x.code === watchEmployeeType)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_EMPLOYEE_TYPE ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                                <Controller name='employeeCategory' control={control} 
                                    
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(25% - 8px)'}} id="employeeCategory"
                                        label={t('Category')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_EMPLOYEE_CATEGORY, 
                                                                  `${t('Employee')} - ${t('Category')}`, `${t('Employee')} - ${t('Category')}`)}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchEmployeeCategory) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_EMPLOYEE_CATEGORY, `${t('Employee')} - ${t('Category')}`, `${t('Employee')} - ${t('Category')}`,
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_EMPLOYEE_CATEGORY && x.code === watchEmployeeCategory)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_EMPLOYEE_CATEGORY ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                            </Box>   
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <Controller name='lastDiploma' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(20% - 8px)'}} id="lastDiploma"
                                        label={t('Last diploma')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_TEACHER_DIPLOMA, 
                                                                  `${t('Diploma')}`, `${t('Diploma')}`)}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchLastDiploma) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_TEACHER_DIPLOMA, `${t('Diploma')}`, `${t('Diploma')}`,
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_TEACHER_DIPLOMA && x.code === watchLastDiploma)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_TEACHER_DIPLOMA ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                /> 
                                <Controller control={control}
                                  name='diplomaDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('Diploma date')} 
                                      onChange={onChange}                     
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(15% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(15% - 8px)'}} />}
                                    /> )}
                                />
                                <Controller name='specialization' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(20% - 8px)'}} id="specialization"
                                        label={t('Specialization')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_TEACHER_SPECIALIZATION, 
                                                                  `${t('Specialization')}`, `${t('Specialization')}`)}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchSpecialization) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_TEACHER_SPECIALIZATION, `${t('Specialization')}`, `${t('Specialization')}`,
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_TEACHER_SPECIALIZATION && x.code === watchSpecialization)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_TEACHER_SPECIALIZATION ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                /> 
                                <Controller name='functionCode' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(20% - 8px)'}} id="functionCode"
                                        label={t('Function')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_TEACHER_FUNCTION, 
                                                                  `${t('Employee')} - ${t('Function')}`, `${t('Employee')} - ${t('Function')}`)}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchFunctionCode) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_TEACHER_FUNCTION, `${t('Employee')} - ${t('Function')}`, `${t('Employee')} - ${t('Function')}`,
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_TEACHER_FUNCTION && x.code === watchFunctionCode)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_TEACHER_FUNCTION ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                />
                                <Controller name='serviceCode' control={control} 
                                    
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(20% - 8px)'}} id="serviceCode"
                                        label={t('Service')} inputProps={ {readOnly: false}} 
                                        helperText={ 
                                          <FormHelperText>                                    
                                            { canAddEnumerationItem && <Link href="#" onClick={(event) => onAddItemClick(Enum_TEACHER_SERVICE, 
                                                                  `${t('Employee')} - ${t('Service')}`, `${t('Employee')} - ${t('Service')}`)}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue', px: 3 }} >
                                              {t('Add')}
                                            </Link> }
                                            { (canUpdateEnumerationItem && !isFalsy(watchServiceCode) ) && <Link href="#" onClick={(event) => {
                                                   onUpdateItemClick( Enum_TEACHER_SERVICE, `${t('Employee')} - ${t('Service')}`, `${t('Employee')} - ${t('Service')}`,
                                                          refEnumItems.current?.find(x => x.enumerationCode === Enum_TEACHER_SERVICE && x.code === watchServiceCode)
                                                        ); }}
                                              sx={{ cursor: 'pointer', textDecoration: 'none', color: 'blue' }} >
                                              {t('Update')}
                                            </Link> }
                                          </FormHelperText>
                                        } >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_TEACHER_SERVICE ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                /> 
                              
                            </Box>
                            <Box sx={{ mt: 0.25, width: '100%' }} > 
                              <Typography variant="h6" id="tableTitle" color="primary" noWrap 
                                    sx={{...typographyGroupBoxStyling}}>
                                {`${t(('Identity'))} `}
                              </Typography>                                                       
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <TextField sx={{width:'calc(15% - 8px)'}} id="personId" label={`${t('Person')} - ${t('Id')}`} 
                                {...register('personId')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                InputProps={{
                                  readOnly: true,
                                  endAdornment: (
                                    <InputAdornment position="end">                                            
                                      <IconButton color="primary" onClick={handleClickOpenPerson}>
                                        <ArrowDropDownCircleIcon />
                                      </IconButton>                                                                                               
                                  </InputAdornment>
                                ) 
                              }} />
                              <TextField sx={{width:'calc(35% - 8px)'}} id="fn" label={lg.startsWith('fr')?t('Last name'):t('First name')} 
                                {...register(lg.startsWith('fr')?'person.lastName':'person.firstName')} 
                                inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } />   
                              <TextField sx={{width:'calc(35% - 8px)'}} id="ln" label={lg.startsWith('fr')?t('First name'):t('Last name')} 
                                {...register(lg.startsWith('fr')?'person.firstName':'person.lastName')}
                                inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }  />  
                              <TextField sx={{width:'calc(15% - 8px)'}} id="particularSign" label={t('Part. sign')} 
                                {...register('person.particularSign')}  />                              
                                { openPersonFilter && <FormDialog open={openPersonFilter} maxWidth='md'
                                  okText='' cancelText='' title={t('Person')} onCancel={()=> {}} 
                                  onClose={()=> {setOpenPersonFilter(false);}} onOk={()=> {setOpenPersonFilter(false);}}  >
                                      <BasicTextFilterForm<IPerson> {...basicFilterPerson } />
                              </FormDialog> }
                            </Box>                                                   
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <Controller name='person.gender' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(15% - 8px)'}} id="gender"
                                        label={t('Gender')} inputProps={ {readOnly: false}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_PERSON_GENDER ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                /> 
                                <Controller name='person.civility' control={control}                                     
                                    render={ ({field: {onChange, value}}) => (
                                      <TextField select onChange={onChange} value={value} sx={{width:'calc(15% - 8px)'}} id="civility"
                                        label={t('Civility')} inputProps={ {readOnly: false}} focused >
                                        {enumItems && enumItems.filter( e => 
                                              e.enumerationCode === Enum_PERSON_CIVILITY ).map( 
                                          (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                        }
                                      </TextField>
                                    )}
                                /> 
                              <Controller control={control}
                                  name='person.birthDate' 
                                  render={({ field: { onChange, onBlur, value, ref } }) => (
                                    <DatePicker label={t('Birth date')} 
                                      onChange={onChange}                     
                                      value={new Date(value)}
                                      slotProps={{ textField: { sx: {width:'calc(20% - 8px)'}  }} }
                                      //renderInput={(params) => <TextField {...params} sx={{width:'calc(20% - 8px)'}} />}
                                    /> )}
                                />
                              <TextField sx={{width:'calc(50% - 8px)'}} id="birthPlace" label={t('Birth place')} 
                                  {...register('person.birthPlace')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } } /> 
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} > 
                              <TextField sx={{width:'calc(15% - 8px)'}} id="portable1" label={`${t('Portable')} 1`} 
                                {...register('person.portable1')} inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }  />
                              <TextField sx={{width:'calc(15% - 8px)'}} id="portable2" label={`${t('Portable')} 2`} 
                                {...register('person.portable2')}  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>   
                              <TextField sx={{width:'calc(35% - 8px)'}} id="email1" label={`${t('Email')} 1`} 
                                {...register('person.email1')}  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>  
                              <TextField sx={{width:'calc(35% - 8px)'}} id="email2" label={`${t('Email')} 2`} 
                                {...register('person.email2')}  inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }/>                                                    
                            </Box>
                            <Box sx={{ mt: 1, width: '100%' }} >
                              <Stack flexDirection='row'>                
                                <Box sx={{ mt: 1, width: '50%' }} >
                                  <input type="file" id="file-upload" style={{ display: "none" }} onChange={setFile}
                                    accept="image/*" />
                                  <div key={'step.id'}>
                                    <Box
                                      component="img"
                                      sx={carouselImage}
                                      //src={`data:image/png;base64,${watchBase64Picture}`}
                                      src={isFalsy(watchBase64Picture)?`/images/person.jpg`:`data:image/png;base64,${watchBase64Picture}`}
                                      onClick={openFileDialog}
                                      //alt={step.id}
                                    ></Box>
                                  </div>
                                  {/* <img  width='100%'
                                    onClick={openFileDialog} height='100%' maxHeight='250px' /> */}
                                </Box>
                                <Box sx={{ mt: 1, width: '50%' }} >
                                  <Stack flexDirection='column'>                
                                    <Box sx={{ mt: 1, width: '100%' }} >
                                      <Controller name='person.defaultLanguage' control={control}                                     
                                            render={ ({field: {onChange, value}}) => (
                                              <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="defaultLanguage"
                                                label={t('Language')} inputProps={ {readOnly: false}} focused >
                                                {enumItems && enumItems.filter( e => 
                                                      e.enumerationCode === Enum_DEFAULT_LANGUAGE ).map( 
                                                  (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                                }
                                              </TextField>
                                            )}
                                        />                                    
                                      <Controller name='person.nationality' control={control}                                     
                                            render={ ({field: {onChange, value}}) => (
                                              <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="nationality"
                                                label={t('Nationality')} inputProps={ {readOnly: false}} focused >
                                                {enumItems && enumItems.filter( e => 
                                                      e.enumerationCode === Enum_COUNTRY ).map( 
                                                  (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                                }
                                              </TextField>
                                            )}
                                        />
                                    </Box>
                                    <Box sx={{ mt: 1, width: '100%' }} >
                                      <Controller name='person.identificationType' control={control}                                     
                                            render={ ({field: {onChange, value}}) => (
                                              <TextField select onChange={onChange} value={value} sx={{width:'calc(50% - 8px)'}} id="defaultLanguage"
                                                label={t('Identification type')} inputProps={ {readOnly: false}} focused >
                                                {enumItems && enumItems.filter( e => 
                                                      e.enumerationCode === Enum_PERSON_IDENTIFICATION ).map( 
                                                  (x,idx) => <MenuItem key={x.code} value={x.code}>{x.name}</MenuItem> )
                                                }
                                              </TextField>
                                            )}
                                        />
                                        <TextField sx={{width:'calc(50% - 8px)'}} id="identificationNumber" label={`${t('Number')}`}
                                          {...register('person.identificationNumber')}  />
                                    </Box>
                                    <Box sx={{ mt: 1, width: '100%' }} >
                                      <TextField sx={{width:'calc(100% - 8px)'}} id="identificationDelivrancePlace" label={`${t('Delivrance place')}`}
                                          {...register('person.identificationDelivrancePlace')}  />
                                    </Box>
                                    <Box sx={{ mt: 1, width: '100%' }} >
                                      <Controller control={control}
                                        name='person.identificationStartDate' 
                                        render={({ field: { onChange, onBlur, value, ref } }) => (
                                          <DatePicker label={t('Start date')} 
                                            onChange={onChange}                     
                                            value={new Date(value)}
                                            slotProps={{ textField: { sx: {width:'calc(50% - 8px)'}  }} }
                                            //renderInput={(params) => <TextField {...params} sx={{width:'calc(50% - 8px)'}} />}
                                          /> )}
                                      />
                                      <Controller control={control}
                                      name='person.identificationEndDate' 
                                      render={({ field: { onChange, onBlur, value, ref } }) => (
                                        <DatePicker label={t('End date')} 
                                          onChange={onChange}                     
                                          value={new Date(value)}
                                          slotProps={{ textField: { sx: {width:'calc(50% - 8px)'}  }} }
                                          //renderInput={(params) => <TextField {...params} sx={{width:'calc(50% - 8px)'}} />}
                                        /> )}
                                    />
                                    </Box>
                                    <Box sx={{ mt: 1, width: '100%' }} >
                                        
                                    </Box>
                                  </Stack>
                                </Box>
                              </Stack>
                            </Box>
                            
                        </Stack>                        
                      </Grid>
                    <Grid item xs={12} md={5} lg={5} component={Paper} sx={{ borderRadius: 2, ml: 0, }} >
                      <Stack flexDirection='column'>                
                        <Box sx={{ mt: 1, width: '100%' }} >
                          <FormControlLabel sx={{width:'calc(55% - 8px)'}}
                              label={t('Has social security ?')}
                              control={
                              <Controller
                                  name='hasSocialSecurity'
                                  control={control}
                                  render={({field: {value, onChange,...props} }) => <Checkbox {...props} checked={value} onChange={onChange} />}                        
                              />} />
                              <TextField sx={{width:'calc(45% - 8px)', display: watchHasSocialSecurity? '' : 'none'}} id="socialSecurityNumber" label={`${t('Number')}`}
                                          inputProps={ { autoComplete: 'new-password', style: {textTransform: 'none'} } }
                                          {...register('socialSecurityNumber')}  />
                        </Box>
                        <Box sx={{ mt: 1, width: '100%' }} >
                          <ArrayFieldTableEx<IEmployee,IEmployeeSalaryItem,'id'> 
                                mainObject={getValues()} fieldKey='id' 
                                headCells={headEmployeeSalaryItemCells} rowsPathName={`employeeSalaryItems` }
                                title={t('Salary items')} rowActionIcon={employeeSalaryItemRowActionIcon}  
                                //onRowSelected={handleQuerySelected}
                                                    
                                refAppend={refAppendEmployeeSalaryItems as MutableRefObject<(value: Partial<FieldArray<ISchoolYear>> | Partial<FieldArray<ISchoolYear>>[], options?: FieldArrayMethodProps) => void>}
                                refUpdate={refUpdateEmployeeSalaryItem as MutableRefObject<(index: number,value: Partial<FieldArray<ISchoolYear>>) => void>}
                                refRemove={refRemoveEmployeeSalaryItem as MutableRefObject<(index: number) => void>}

                                //stateSelected={[selectedRoleEntities, setSelectedRoleEntities]}
                                //displayMore={undefined}
                                toolbarActions={[
                                //{ toolTip: `${t('Add')}...`, onClickIcon: handleAddNewBillings ,icon: AddCircleIcon,  },
                                { toolTip: `${t('Add')}...`, onClickIcon: handleAddEmployeeSalaryItems ,icon: AddCircleIcon,  },

                                ]}
                              /> 
                        </Box>

                        { openEntityExpression && <FormDialog open={openEntityExpression} maxWidth='md'
                                okText={t('OK')} cancelText='' title={t('Expression')} onCancel={()=> {}} 
                                onClose={()=> {setOpenEntityExpression(false);}} onOk={handleClickOkExpression}  >
                                <EntityExpression entityName={currentEntityName} properties={[]} 
                                  expression={currentExpression} setExpression={setCurrentExpression} />
                              </FormDialog>  }
                        
                        { displayEnumerationItemDialog && <DialogEnumerationItemForm 
                              {...{open: displayEnumerationItemDialog, 
                                    title: '', 
                                    enumerationItem, hideEnumerationItemDialog, saveEnumerationItem,
                                    afterSave: async () => {await refetchEnumerationItems()}}} 
                              />
                          }
                      </Stack>
                    </Grid>     
                                                  
                </Grid>
            </Box>
        </FormProvider> 
  )
}

